/**
 * ActivityPeriodSelector Component
 *
 * Period selection and date navigation - same logic as Analytics PeriodSelector.
 * Includes: Period dropdown (All time, Daily, Weekly, Monthly, Yearly, Custom),
 * date inputs via DateRangePicker, and left/right arrows for interval navigation.
 */

import { ReactElement } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DateRangePicker from "./DateRangePicker";

export interface ActivityPeriodSelectorProps {
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onPeriodNavigation: (direction: "left" | "right") => void;
  focusedDateField: "startDate" | "endDate" | null;
  onFocusedDateFieldChange: (field: "startDate" | "endDate" | null) => void;
}

/**
 * Period selector with date range picker and arrow navigation
 */
export default function ActivityPeriodSelector({
  selectedPeriod,
  onPeriodChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onPeriodNavigation,
  focusedDateField,
  onFocusedDateFieldChange,
}: ActivityPeriodSelectorProps): ReactElement {
  const isAllTime = selectedPeriod === "All time";

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", width: "100%" }}>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Period</InputLabel>
        <Select
          value={selectedPeriod}
          label="Period"
          onChange={(e) => onPeriodChange(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="All time">All time</MenuItem>
          <MenuItem value="Daily">Daily</MenuItem>
          <MenuItem value="Weekly">Weekly</MenuItem>
          <MenuItem value="Monthly">Monthly</MenuItem>
          <MenuItem value="Yearly">Yearly</MenuItem>
          <MenuItem value="Custom">Custom Period</MenuItem>
        </Select>
      </FormControl>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          size="small"
          onClick={() => onPeriodNavigation("left")}
          disabled={isAllTime}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            minWidth: 40,
            minHeight: 40,
          }}
          aria-label="Previous period"
        >
          <ChevronLeftIcon />
        </IconButton>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          focusedDateField={focusedDateField}
          onFocusedDateFieldChange={onFocusedDateFieldChange}
        />
        <IconButton
          size="small"
          onClick={() => onPeriodNavigation("right")}
          disabled={isAllTime}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            minWidth: 40,
            minHeight: 40,
          }}
          aria-label="Next period"
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}
